import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { externalEntityService } from "../../services/externalEntityService"
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from "../common/Loader"
import Alert from "../common/Alert"
import { ArrowLeft, CreditCard } from "lucide-react"
import Button from '../common/Button.js'
import ExternalShipmentPaymentModal from "./external_shipment_payment/ExternalShipmentPaymentModal"
import { useTranslation } from 'react-i18next';
import { formatUsernameLocation } from '../../utils/userDisplay';


const ViewExternalShipment = () => {
  // Route: /external-ml/:externalMLId/shipment/:shipmentId
  // shipmentId here is the external_shipment_id (string like "SI000001")
  const { externalMLId, shipmentId } = useParams()
  const navigate = useNavigate()
  const [shipment, setShipment] = useState(null)
  const [paymentHistory, setPaymentHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [showPaymentModal, setShowPaymentModal] = useState(false)

  useEffect(() => {
    fetchShipmentData()
  }, [shipmentId])

  const fetchShipmentData = async () => {
    try {
      setLoading(true)

      // GET /api/external-entities/shipments/<external_shipment_id>
      // Response: { success, shipment: {...}, payment_history: [...] }
      const response = await externalEntityService.getExternalShipment(shipmentId)
      const data = response.data

      setShipment(data.shipment || null)
      setPaymentHistory(Array.isArray(data.payment_history) ? data.payment_history : [])
    } catch (error) {
      console.error("Error fetching External Shipment:", error)
      Alert.error(error.response?.data?.message || "Failed to load shipment details")
      navigate(`/external-ml/view/${externalMLId}`)
    } finally {
      setLoading(false)
    }
  }

  const getPaymentModeColor = (mode) => {
    const map = {
      to_pay: "bg-yellow-100 text-yellow-800",
      paid: "bg-green-100 text-green-800",
      paid_balance: "bg-blue-100 text-blue-800",
    }
    return map[(mode || "").toLowerCase()] || "bg-gray-100 text-gray-800"
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading Shipment Details..." />
        </div>
      </DashboardLayout>
    )
  }

  if (!shipment) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <p className="text-gray-600 text-lg font-semibold">Shipment not found</p>
            <button
              onClick={() => navigate(`/external-ml/view/${externalMLId}`)}
              className="mt-4 px-6 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 font-semibold"
            >
              Back to External ML
            </button>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>

                {/* Page Header */}
                <div className="p-4 sm:p-2 sm:py-0 mb-6">
                  <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                    <div>
                      <h1 className="text-2xl sm:text-xl font-bold text-secondary">
                        Shipment #{shipment.external_shipment_id}
                      </h1>
                      <p className="text-gray-700 text-sm sm:text-base">
                        LR No: {shipment.lr_no} · ML Sheet: {externalMLId}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowPaymentModal(true)}
                        className="flex items-center gap-2 px-3 py-2.5 sm:py-2 text-xs sm:text-sm bg-orange-500 hover:bg-orange-600 text-white rounded-xl transition-all duration-300 font-medium whitespace-nowrap"
                      >
                        <CreditCard className="w-4 h-4" /> Manage Payments
                      </button>
                      <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate(`/external-ml/view/${externalMLId}`)}>Back</Button>
                    </div>
                  </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-orange-500">
                    <div className="text-gray-500 text-xs font-semibold uppercase mb-1">Total Amount</div>
                    <div className="text-xl font-bold text-secondary">
                      ₹{(shipment.total_amount || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-green-500">
                    <div className="text-gray-500 text-xs font-semibold uppercase mb-1">Paid Amount</div>
                    <div className="text-xl font-bold text-green-600">
                      ₹{(shipment.paid_amount || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-red-400">
                    <div className="text-gray-500 text-xs font-semibold uppercase mb-1">Balance</div>
                    <div className="text-xl font-bold text-red-500">
                      ₹{(shipment.balance_amount || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-blue-500">
                    <div className="text-gray-500 text-xs font-semibold uppercase mb-1">Payment Mode</div>
                    <span
                      className={`px-2 py-1 text-sm rounded inline-block mt-1 ${getPaymentModeColor(
                        shipment.payment_mode
                      )}`}
                    >
                      {shipment.payment_mode || "—"}
                    </span>
                  </div>
                </div>

                {/* Shipment Details */}
                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md mb-6">
                  <h3 className="text-sm font-semibold text-secondary mb-4 pb-2 border-b border-gray-200">
                    Shipment Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-gray-50 rounded-xl p-3 border border-primary">
                      <div className="text-gray-500 text-xs mb-1">Shipment ID</div>
                      <div className="text-sm text-secondary font-regular">{shipment.external_shipment_id}</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 border border-primary">
                      <div className="text-gray-500 text-xs mb-1">LR No</div>
                      <div className="text-sm text-secondary font-regular">{shipment.lr_no || "—"}</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 border border-primary">
                      <div className="text-gray-500 text-xs mb-1">To Station</div>
                      <div className="text-sm text-secondary font-regular">{shipment.to_station || "—"}</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 border border-primary">
                      <div className="text-gray-500 text-xs mb-1">Consignor</div>
                      <div className="text-sm text-secondary font-regular">{shipment.consignor || "—"}</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 border border-primary">
                      <div className="text-gray-500 text-xs mb-1">Consignee</div>
                      <div className="text-sm text-secondary font-regular">{shipment.consignee || "—"}</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 border border-primary">
                      <div className="text-gray-500 text-xs mb-1">Consignee Contact</div>
                      <div className="text-sm text-secondary font-regular">{shipment.consignee_no || "—"}</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 border border-primary">
                      <div className="text-gray-500 text-xs mb-1">Quantity</div>
                      <div className="text-sm text-secondary font-regular">{shipment.quantity ?? "—"}</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 border border-primary">
                      <div className="text-gray-500 text-xs mb-1">Service Charge (SC.Rs)</div>
                      <div className="text-sm text-secondary font-regular">
                        ₹{(shipment.sc_rs || 0).toFixed(2)}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 border border-primary">
                      <div className="text-gray-500 text-xs mb-1">Created At</div>
                      <div className="text-sm text-secondary font-regular">
                        {shipment.created_at
                          ? new Date(shipment.created_at).toLocaleString("en-IN")
                          : "—"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment History */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-secondary">
                      Payment History ({paymentHistory.length})
                    </h3>
                    <button
                      onClick={() => setShowPaymentModal(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold rounded-xl transition-colors"
                    >
                      <CreditCard className="w-3.5 h-3.5" /> Manage
                    </button>
                  </div>

                  {paymentHistory.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500 font-semibold">No payment records found</p>
                    </div>
                  ) : (
                    <>
                      {/* Desktop */}
                      <div className="hidden md:block w-full overflow-x-auto">
                        <table className="w-full table-fixed border-collapse">
                          <thead>
                            <tr className="bg-primary text-tertiary">
                              <th className="px-4 py-3 text-xs font-semibold w-[5%] text-center">#</th>
                              <th className="px-4 py-3 text-xs font-semibold text-center">Amount</th>
                              <th className="px-4 py-3 text-xs font-semibold text-center">Mode</th>
                              <th className="px-4 py-3 text-xs font-semibold text-center">Prev Balance</th>
                              <th className="px-4 py-3 text-xs font-semibold text-center">New Balance</th>
                              <th className="px-4 py-3 text-xs font-semibold text-center">Date</th>
                              <th className="px-4 py-3 text-xs font-semibold text-center">Recorded By</th>
                              <th className="px-4 py-3 text-xs font-semibold text-center">Remarks</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {paymentHistory.map((p, i) => (
                              <tr
                                key={p.id}
                                className={`hover:bg-gray-50 text-center ${
                                  i % 2 === 0 ? "bg-white" : "bg-gray-50"
                                }`}
                              >
                                <td className="px-5 py-3 text-sm">{i + 1}</td>
                                <td className="px-5 py-3 text-sm font-semibold text-green-600">
                                  ₹{(p.payment_amount || 0).toFixed(2)}
                                </td>
                                <td className="px-5 py-3 text-sm text-gray-600">{p.payment_mode || "—"}</td>
                                <td className="px-5 py-3 text-sm text-gray-600">
                                  ₹{(p.previous_balance || 0).toFixed(2)}
                                </td>
                                <td className="px-5 py-3 text-sm text-orange-600 font-semibold">
                                  ₹{(p.new_balance || 0).toFixed(2)}
                                </td>
                                <td className="px-5 py-3 text-sm text-gray-600">
                                  {p.payment_date
                                    ? new Date(p.payment_date).toLocaleDateString("en-IN")
                                    : "—"}
                                </td>
                                <td className="px-5 py-3 text-sm text-gray-600">
                                  {p.recorded_by_display
                                    || formatUsernameLocation(
                                      p.recorded_by_name || p.recorded_by_username,
                                      p.recorded_by_location,
                                      p.recorded_by_name || p.recorded_by_user_id || "—"
                                    )}
                                </td>
                                <td className="px-5 py-3 text-sm text-gray-600 break-all">
                                  {p.remarks || "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile Cards */}
                      <div className="md:hidden divide-y divide-gray-200">
                        {paymentHistory.map((p, i) => (
                          <div key={p.id} className="p-4">
                            <div className="flex justify-between mb-1">
                              <span className="text-sm font-semibold text-green-600">
                                ₹{(p.payment_amount || 0).toFixed(2)}
                              </span>
                              <span className="text-xs text-gray-500">
                                {p.payment_date
                                  ? new Date(p.payment_date).toLocaleDateString("en-IN")
                                  : "—"}
                              </span>
                            </div>
                            <div className="text-xs text-gray-600 space-y-1">
                              <div>Mode: {p.payment_mode || "—"}</div>
                              <div>Prev Balance: ₹{(p.previous_balance || 0).toFixed(2)}</div>
                              <div>New Balance: ₹{(p.new_balance || 0).toFixed(2)}</div>
                              <div>
                                By:{" "}
                                {p.recorded_by_display
                                  || formatUsernameLocation(
                                    p.recorded_by_name || p.recorded_by_username,
                                    p.recorded_by_location,
                                    p.recorded_by_name || "—"
                                  )}
                              </div>
                              {p.remarks && <div>Remarks: {p.remarks}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>

      {showPaymentModal && (
        <ExternalShipmentPaymentModal
          shipmentId={shipmentId}
          shipment={shipment}
          onClose={() => setShowPaymentModal(false)}
          onPaymentSaved={fetchShipmentData}
        />
      )}
    </DashboardLayout>
  )
}

export default ViewExternalShipment